import React, { Component, type ErrorInfo, type ReactNode } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ErrorBoundaryProps {
  /** Subtree to monitor for errors. */
  children: ReactNode
  /**
   * Optional custom fallback. When provided, rendered instead of the
   * built-in fallback UI.  Receives the error and a reset callback.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode
  /**
   * Optional callback fired when an error is caught.
   * Useful for sending errors to an error reporting service.
   */
  onError?: (error: Error, info: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

// ── Helper components ─────────────────────────────────────────────────────────

const TerminalIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-8 w-8"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
)

const RefreshIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
)

// ── Built-in fallback UI ──────────────────────────────────────────────────────

interface BuiltInFallbackProps {
  error: Error
  onReset: () => void
}

const BuiltInFallback: React.FC<BuiltInFallbackProps> = ({ error, onReset }) => (
  <div
    role="alert"
    aria-live="assertive"
    className="flex items-center justify-center min-h-64 p-8 bg-neutral-900"
  >
    <div
      className={[
        'w-full max-w-lg',
        'rounded-xl border border-error-800/60 bg-neutral-800/60',
        'overflow-hidden',
      ].join(' ')}
    >
      {/* Header strip */}
      <div className="flex items-center gap-3 px-5 py-3.5 bg-error-900/40 border-b border-error-800/40">
        {/* Traffic-light dots */}
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="h-3 w-3 rounded-full bg-error-500" />
          <span className="h-3 w-3 rounded-full bg-warning-500" />
          <span className="h-3 w-3 rounded-full bg-success-600" />
        </span>
        <span className="ml-1 text-xs font-mono text-neutral-500">error boundary</span>
      </div>

      {/* Body */}
      <div className="px-6 py-6 flex flex-col gap-5">
        {/* Icon + title */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 text-error-400 mt-0.5">
            <TerminalIcon />
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold text-neutral-100">
              An unexpected error occurred
            </h2>
            <p className="text-sm text-neutral-400 leading-relaxed">
              This component crashed. The rest of the application is unaffected.
              You can try resetting the component or refreshing the page.
            </p>
          </div>
        </div>

        {/* Error detail block */}
        {error.message && (
          <div className="rounded-lg bg-neutral-900 border border-neutral-700 overflow-x-auto">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-700">
              <span className="h-1.5 w-1.5 rounded-full bg-error-500" aria-hidden="true" />
              <span className="text-2xs font-mono font-semibold uppercase tracking-widest text-neutral-500">
                {error.name ?? 'Error'}
              </span>
            </div>
            <pre className="px-4 py-3 text-xs font-mono text-error-300 leading-relaxed whitespace-pre-wrap break-words">
              {error.message}
            </pre>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={onReset}
            className={[
              'inline-flex items-center gap-2',
              'h-9 px-4 rounded-md',
              'text-sm font-medium',
              'bg-neutral-700 text-neutral-100',
              'border border-neutral-600',
              'hover:bg-neutral-600 hover:border-neutral-500',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-800',
              'transition-colors duration-fast',
            ].join(' ')}
          >
            <RefreshIcon />
            Reset component
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={[
              'inline-flex items-center gap-2',
              'h-9 px-4 rounded-md',
              'text-sm font-medium',
              'text-neutral-400',
              'border border-transparent',
              'hover:bg-neutral-800 hover:text-neutral-100',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-800',
              'transition-colors duration-fast',
            ].join(' ')}
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  </div>
)

// ── Class component ───────────────────────────────────────────────────────────

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
    this.handleReset = this.handleReset.bind(this)
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, info)
    }
  }

  handleReset(): void {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    const { hasError, error } = this.state
    const { children, fallback } = this.props

    if (!hasError || error === null) {
      return children
    }

    if (fallback) {
      return fallback(error, this.handleReset)
    }

    return <BuiltInFallback error={error} onReset={this.handleReset} />
  }
}

export default ErrorBoundary
